import React, { useState, useCallback, useEffect, useMemo } from 'react'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import '../NewsDialog/NewsDialog.scss'
import { IARRte } from '../iar-rte'
import ChooseImage from '../ChooseImage'
import { useCMSContext } from '../../CMS/CMSContext'
import { useMenuSectionContext } from '../../CMS/MenuSection/MenuSectionContext'
import ChooseVideo from '../ChooseVideo'
import CustomDropdown from '../Dropdown/dropdown'
import MediaThumbnail from '../../Shared/MediaThumbnail/MediaThumbnail'
import ButtonSave from '../ButtonSave/ButtonSave'
import MediaFilesMessage from '../MediaFilesMessage'
import OnlineVideos from '../../Shared/OnlineVideos'
import { useErrorSnackBarContext } from '../ErrorSnackBar/ErrorSnackBarContext'
import { videoTypes } from '../../../Constants/videoTypes'
import { IARDialog } from '../../Shared/Dialog/IARDialog'
import Interceptor from '../../../Services/serviceBase.interceptor'
import serviceBase from 'app/Services/serviceBase'
import PrepublishButtons from 'app/Components/Shared/PrepublishButtons/PrepublishButtons'
import {
  handleChange,
  removeOnlineVideo,
  removeAsset,
  getMarketingSectionById,
  useEffectAfterMount,
  INITIAL_STATE,
  getImagesPayload,
  processAndGetImages,
  getOnlineVideosPayload,
  getVideosPayload,
  getClonedImagesPayload,
  getClonedVideosPayload,
  saveMarketingSection,
  saveMarketingSectionPreview,
  getMarketingSectionByName,
  getMarketingSectionPreviewByName,
  getMarketingSectionPreviewById,
  publish
} from './MarketingDialogFunctions'
import { EmptyTagsTypes } from '../../../Constants/bodyTextValidationsTypes'
import { saveSectionVideoProcess } from 'app/Services/serviceSectionsMediaShared'
import { EncondingSectionType } from 'app/Constants/EncodingSectionType'
import { cloneDeep } from 'lodash'

function MarketingDialog({
  title = 'Marketing Custom Section',
  id = null,
  isCreateMarketingSection = true,
  name = null,
  isOpen,
  onClose,
  marketingOptions,
  setMarketingSections,
  rowName = null,
  isClone = false,
  nameToClone,
  identifierToClone,
  emergencyProviderCloneName = null,
  fetchSections,
  disableField,
  showPublished,
  updateListAfterPublishPreview
}) {
  const [dialogState, setDialogState] = useState(INITIAL_STATE)
  const [localState, setLocalState] = useState({
    discardDialogOpen: false,
    posting: false,
    errorLabel: '',
    backUpState: INITIAL_STATE,
    isMounted: true,
    errorBodyText: '',
    errorTitle: '',
    deletedFiles: [],
    isCreateFromDialog: true,
    filesToUpdate: []
  })
  const [bodyText, setBodyText] = useState('')

  useEffect(() => {
    setBodyText(dialogState.bodyText || '')
  }, [dialogState._id, dialogState.bodyText])

  const { errorSnackBarActions } = useErrorSnackBarContext()
  Interceptor.interceptor(errorSnackBarActions)
  const cmsContext = useCMSContext()
  const menuContext = useMenuSectionContext()
  const { departmentTimeBalance, iarMember, socket, appConfiguration, newVideoEncoding } =
    cmsContext.state
  const { marketingSections } = menuContext.state
  const { setMenuMarketingSections } = menuContext.actions
  const [existingMarketingSection, setExistingMarketingSection] = useState(null)
  const [mcsToCloneExist, setMcsToCloneExist] = useState(false)
  const [disabledButton, setDisabledButton] = useState(false)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isPreviewNotSaveDialogOpen, setIsPreviewNotSavedDialogOpen] = useState(false)
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false)

  useEffect(() => {
    const fetch = async () => {
      if (id && id.length > 0) {
        let res = null
        if (!showPublished && appConfiguration.previewEnabled && !disableField)
          res = await getMarketingSectionPreviewById(id)
        else res = await getMarketingSectionById(id)
        if (res) {
          setLocalState({ ...localState, backUpState: cloneDeep(res) })
          setDialogState(cloneDeep(res))
          return
        }
        await fetchSections()
        errorSnackBarActions.setOpen(
          true,
          'This marketing section was already deleted',
          'warning',
          7000
        )
      } else {
        if (rowName.length > 0) {
          nameFetch(rowName)
        }
      }
    }

    const nameFetch = async (name) => {
      let response = null
      if (!showPublished && appConfiguration.previewEnabled && !disableField)
        response = await getMarketingSectionPreviewByName(name)
      else response = await getMarketingSectionByName(name)
      if (response.status === 200) {
        const isCreating = isClone ? !mcsToCloneExist : false
        setLocalState({
          ...localState,
          backUpState: cloneDeep(response.data),
          isCreateFromDialog: isCreating
        })
        setDialogState(cloneDeep(response.data))
        isCreateMarketingSection = isCreating
      }
    }

    const nameFetchForClone = async (name) => {
      let response = null

      if (!showPublished && appConfiguration.previewEnabled && !disableField)
        response = await getMarketingSectionPreviewByName(name)
      else response = await getMarketingSectionByName(name)
      if (response.status === 200) {
        setExistingMarketingSection(cloneDeep(response.data))
        setMcsToCloneExist(true)
      }
      nameFetch(identifierToClone) //get the section to clone
    }
    if (localState.isMounted && !isCreateMarketingSection && !isClone) {
      fetch()
    } else {
      if (isClone) {
        nameFetchForClone(name) // get the section to create to be set by the clone
      } else {
        nameFetch(name)
      }
    }
    return () => {
      setLocalState({ ...localState, isMounted: false })
    }
  }, [showPublished, disableField])

  useEffect(() => {
    if (socket) {
      socket.on(`marketing_section_MCS_dialog_${id}`, (args) => {
        console.log(`marketing_section_MCS_dialog_${id}`, args, new Date())
        if (args.param === 'start') {
          console.log('capture emit-start')
          setDisabledButton(true)
        }
        if (args.param === 'error') {
          console.log('Ocurred an error')
          setDisabledButton(false)
        }
        if (args.param === 'saved') {
          console.log('saved MCS')
          setDisabledButton(false)
        }
      })
    }
    return () => {
      socket.off(`marketing_section_MCS_dialog_${id}`)
    }
  }, [socket])

  useEffectAfterMount(() => {
    if (localState.errorLabel && localState.errorLabel.length > 0) {
      errorSnackBarActions.setOpen(true, localState.errorLabel)
      setLocalState((prevState) => ({
        ...prevState,
        errorLabel: ''
      }))
    }
  }, [localState.errorLabel])

  const rteChangeHandler = (content) => {
    setBodyText(content.trim())
  }

  const isNewVideoEncodingProcessActive = useMemo(() => {
    return (
      newVideoEncoding &&
      dialogState.videos?.some(
        (x) => !localState.backUpState.videos.some((v) => v.fileName === x.fileName)
      )
    )
  }, [newVideoEncoding, dialogState.videos, localState.backUpState.videos])

  const handleDropdownChange = useCallback((evt) => {
    const value = evt.target.value
    setDialogState((prevState) => ({ ...prevState, positionFiles: value }))
  }, [])

  const updateAsset = useCallback(
    (file) => {
      if (localState.filesToUpdate.filter((el) => el.fileName === file.fileName).length > 0) {
        localState.filesToUpdate.filter((el) => el.fileName === file.fileName)[0].caption =
          file.caption
      } else {
        setLocalState((prevState) => ({
          ...prevState,
          filesToUpdate: [
            ...prevState.filesToUpdate,
            { caption: file.caption, fileName: file.fileName }
          ]
        }))
      }
    },
    [localState.filesToUpdate]
  )

  const processSectionVideoProcessSaving = async (isPreview) => {
    setDisabledButton(true)
    let readyToSave = isReadyToSave()

    if (!readyToSave) return

    // Socket IO
    //await emitSocketMessage()

    // Creating payload data
    const imagesPayload = processAndGetImages(dialogState.images, localState.backUpState.images)
    const { onlineVideosPayload } = getOnlineVideosPayload(
      dialogState.onlineVideos,
      localState.backUpState.onlineVideos
    )
    let videos = getVideosPayload(dialogState.videos, localState.backUpState.videos)

    let data = {
      title: dialogState.title,
      richText: bodyText,
      type: isPreview
        ? EncondingSectionType.MarketingCustomSectionPreview
        : EncondingSectionType.MarketingCustomSection,
      positionFiles:
        dialogState && dialogState.positionFiles ? parseInt(dialogState.positionFiles) : 0,
      sectionActive: true,
      name: dialogState.name ? dialogState.name : name,
      emergencyProviderName:
        name === 'All'
          ? 'All'
          : dialogState.emergencyProviderName
          ? dialogState.emergencyProviderName
          : marketingOptions.emergencyProviderName,
      country: (dialogState.country ? dialogState.country : marketingOptions.country) || 0,
      state: dialogState.state ? dialogState.state : marketingOptions.state,
      county: dialogState.county ? dialogState.county : marketingOptions.county,
      images: imagesPayload,
      videos: [],
      identifier: '0',
      onlineVideos: onlineVideosPayload,
      updatedFiles: localState.filesToUpdate,
      deletedFiles: localState.deletedFiles,
      memberId: iarMember.memberId,
      contentId: dialogState._id || id
    }

    if (isClone) {
      const cloningData = {
        clonedSectionName: dialogState.emergencyProviderName,
        imagesCloned: getClonedImagesPayload(dialogState.images, localState.backUpState.images),
        videosCloned: getClonedVideosPayload(dialogState.videos, localState.backUpState.videos)
      }
      const dataToOverwrite = {
        ...data,
        ...marketingOptions,
        contentId: existingMarketingSection?._id ?? null,
        name: name,
        cloningData
      }
      data = dataToOverwrite
    }

    // Set number of new videos
    if (videos && videos.length) data.videosNumber = videos.length

    //'emit-start -> socket'
    let url = `${window._env_.COMMUNITY_API_URL}/marketing-custom-section`
    serviceBase.get(`${url}/emit-start?id=${data.contentId}`)

    // Saving section video process
    const response = await saveSectionVideoProcess(data, videos, dialogState.images)
    onClose()

    // Section video process response validation
    if (!response.sectionVideoProcessResponse) {
      setLocalState((prevState) => ({
        ...prevState,
        posting: false
      }))
      errorSnackBarActions.setOpen(true)
    }
    // Encoder call response validation
    if (response.encoderErrors && response.encoderErrors.length) {
      setLocalState((prevState) => ({
        ...prevState,
        errorLabel: ''
      }))
      errorSnackBarActions.setOpen(true, localState.errorLabel)
    }
    setLocalState((prevState) => ({
      ...prevState,
      deletedFiles: []
    }))
  }

  const isReadyToSave = () => {
    setLocalState((prevState) => ({
      ...prevState,
      posting: true
    }))
    const richText = bodyText || dialogState.bodyText
    if (!richText || EmptyTagsTypes.EmptyTag.includes(richText)) {
      setLocalState((prevState) => ({
        ...prevState,
        errorBodyText: 'This field is required!',
        posting: false
      }))

      setTimeout(() => {
        setLocalState((prevState) => ({
          ...prevState,
          errorBodyText: ''
        }))
      }, 5000)
      return false
    }

    if (dialogState.title.length === 0) {
      setLocalState((prevState) => ({
        ...prevState,
        errorTitle: 'This field is required!',
        posting: false
      }))

      setTimeout(() => {
        setLocalState((prevState) => ({
          ...prevState,
          errorTitle: ''
        }))
      }, 5000)
      return false
    }
    return true
  }

  const save = () => {
    setDisabledButton(true)
    let readyToSave = isReadyToSave()

    const { imagesPayload } = getImagesPayload(dialogState.images, localState.backUpState.images)
    const { onlineVideosPayload } = getOnlineVideosPayload(
      dialogState.onlineVideos,
      localState.backUpState.onlineVideos
    )

    let data = {
      _id: dialogState._id ? dialogState._id : id,
      title: dialogState.title,
      bodyText,
      positionFiles:
        dialogState && dialogState.positionFiles ? parseInt(dialogState.positionFiles) : 0,
      sectionActive: true,
      name: dialogState.name ? dialogState.name : name,
      emergencyProviderName:
        name === 'All'
          ? 'All'
          : dialogState.emergencyProviderName
          ? dialogState.emergencyProviderName
          : marketingOptions.emergencyProviderName,
      country: dialogState.country ? dialogState.country : marketingOptions.country,
      state: dialogState.state ? dialogState.state : marketingOptions.state,
      county: dialogState.county ? dialogState.county : marketingOptions.county,
      images: imagesPayload,
      videos: [],
      onlineVideos: onlineVideosPayload,
      filesToUpdate: isClone ? null : localState.filesToUpdate,
      filesToDelete: isClone ? null : localState.deletedFiles
    }

    if (isClone) {
      const id =
        existingMarketingSection && existingMarketingSection._id
          ? existingMarketingSection._id
          : null
      const cloningData = {
        clonedSectionName: dialogState.emergencyProviderName,
        images: getClonedImagesPayload(dialogState.images, localState.backUpState.images),
        videos: getClonedVideosPayload(dialogState.videos, localState.backUpState.videos)
      }
      const dataToOverwrite = {
        ...data,
        ...marketingOptions,
        _id: id,
        name: name,
        cloningData
      }
      data = dataToOverwrite
    }

    const formData = new FormData()

    formData.append('request', JSON.stringify(data))

    dialogState.images.forEach((image) => {
      if (image.file === undefined) return

      formData.append('images', image.file, image.originalName)
      image.file = null
    })
    if (readyToSave) {
      saveMarketingSection(
        formData,
        iarMember,
        setLocalState,
        onClose,
        errorSnackBarActions,
        setMarketingSections,
        isClone ? !mcsToCloneExist : isCreateMarketingSection,
        isClone ? !mcsToCloneExist : localState.isCreateFromDialog,
        marketingSections[0],
        setMenuMarketingSections,
        data._id
      )
    }
  }

  const savePreview = () => {
    setDisabledButton(true)
    let readyToSave = isReadyToSave()

    const { imagesPayload } = getImagesPayload(dialogState.images, localState.backUpState.images)
    const { onlineVideosPayload } = getOnlineVideosPayload(
      dialogState.onlineVideos,
      localState.backUpState.onlineVideos
    )

    let data = {
      _id: dialogState._id ? dialogState._id : id,
      title: dialogState.title,
      bodyText,
      positionFiles:
        dialogState && dialogState.positionFiles ? parseInt(dialogState.positionFiles) : 0,
      sectionActive: true,
      name: dialogState.name ? dialogState.name : name,
      emergencyProviderName:
        name === 'All'
          ? 'All'
          : dialogState.emergencyProviderName
          ? dialogState.emergencyProviderName
          : marketingOptions.emergencyProviderName,
      country: dialogState.country ? dialogState.country : marketingOptions.country,
      state: dialogState.state ? dialogState.state : marketingOptions.state,
      county: dialogState.county ? dialogState.county : marketingOptions.county,
      images: imagesPayload,
      videos: [],
      onlineVideos: onlineVideosPayload,
      filesToUpdate: isClone ? null : localState.filesToUpdate,
      filesToDelete: isClone ? null : localState.deletedFiles
    }

    if (isClone) {
      const id =
        existingMarketingSection && existingMarketingSection._id
          ? existingMarketingSection._id
          : null
      const cloningData = {
        clonedSectionName: dialogState.emergencyProviderName,
        images: getClonedImagesPayload(dialogState.images, localState.backUpState.images),
        videos: getClonedVideosPayload(dialogState.videos, localState.backUpState.videos)
      }
      const dataToOverwrite = {
        ...data,
        ...marketingOptions,
        _id: id,
        name: name,
        cloningData
      }
      data = dataToOverwrite
    }

    const formData = new FormData()

    formData.append('request', JSON.stringify(data))

    dialogState.images.forEach((image) => {
      if (image.file === undefined) return

      formData.append('images', image.file, image.originalName)
      image.file = null
    })
    if (readyToSave) {
      saveMarketingSectionPreview(
        formData,
        iarMember,
        setLocalState,
        onClose,
        errorSnackBarActions,
        isClone ? !mcsToCloneExist : isCreateMarketingSection,
        isClone ? !mcsToCloneExist : localState.isCreateFromDialog,
        marketingSections[0],
        setMenuMarketingSections,
        data._id
      )
    }
  }

  const renderRichTextControl = useCallback(() => {
    if (dialogState.isDiscarded) {
      return (
        <IARRte
          onChange={rteChangeHandler}
          content={dialogState.bodyText}
          discarded={dialogState.isDiscarded}
          disableHandleChange={disableField && !isCreateMarketingSection}
        />
      )
    } else {
      return (
        <IARRte
          onChange={rteChangeHandler}
          content={dialogState.bodyText}
          discarded={dialogState.isDiscarded}
          disableHandleChange={disableField && !isCreateMarketingSection}
        />
      )
    }
  }, [dialogState, rteChangeHandler])

  const publishButtonValidations = () => {
    if (
      JSON.stringify(dialogState) !== JSON.stringify(localState.backUpState) ||
      bodyText !== localState.backUpState.bodyText ||
      JSON.stringify(dialogState.videos) !== JSON.stringify(localState.backUpState.videos) ||
      JSON.stringify(dialogState.images) !== JSON.stringify(localState.backUpState.images) ||
      JSON.stringify(dialogState.onlineVideos) !==
        JSON.stringify(localState.backUpState.onlineVideos)
    ) {
      openNotSavedPreviewDialog()
    } else {
      openSaveDialog()
    }
  }

  const savePublish = async () => {
    try {
      closeSaveDialog()
      setLocalState((prevState) => ({
        ...prevState,
        posting: true
      }))

      const MarketingPublished = await publish(dialogState._id)
      const marketingDiscard = cloneDeep(MarketingPublished)

      setLocalState((prevState) => ({
        ...prevState,
        backUpState: marketingDiscard
      }))

      setDialogState({
        ...MarketingPublished,
        images: MarketingPublished.images ?? [],
        videos: MarketingPublished.videos ?? [],
        onlineVideos: MarketingPublished.onlineVideos ?? []
      })

      setLocalState((prevState) => ({
        ...prevState,
        deletedFiles: [],
        filesToUpdate: []
      }))

      await updateListAfterPublishPreview()
    } catch (error) {
      setLocalState((prevState) => ({
        ...prevState,
        errorLabel: 'There was an error publishing Marketing section'
      }))
      errorSnackBarActions.setOpen(true, localState.errorLabel)
    } finally {
      setLocalState((prevState) => ({
        ...prevState,
        posting: false
      }))
      onClose()
    }
  }

  const closeSaveDialog = () => {
    setIsSaveDialogOpen(false)
  }

  const openSaveDialog = () => {
    setIsSaveDialogOpen(true)
  }

  const openNotSavedPreviewDialog = () => {
    setIsPreviewNotSavedDialogOpen(true)
  }

  const closeNotSavedPreviewDialog = () => {
    setIsPreviewNotSavedDialogOpen(false)
  }

  const closeDiscardDialog = () => {
    setLocalState((prevState) => ({
      ...prevState,
      discardDialogOpen: false
    }))
  }

  const openDiscardDialog = () => {
    setLocalState((prevState) => ({
      ...prevState,
      discardDialogOpen: openDiscardDialog
    }))
  }

  const closePreviewDialog = useCallback(() => {
    setIsPreviewDialogOpen(false)
  }, [])

  const openPreviewDialog = useCallback(() => {
    setIsPreviewDialogOpen(true)
  }, [])

  const handleSavePreview = () => {
    closePreviewDialog()
    if (isNewVideoEncodingProcessActive) {
      processSectionVideoProcessSaving(true)
    } else {
      savePreview()
    }
  }

  const handlerDiscardChanges = () => {
    const clonedMarketing = cloneDeep(localState.backUpState)
    const clonedImages = cloneDeep(localState.backUpState.images || [])
    const clonedVideos = cloneDeep(localState.backUpState.videos || [])
    const clonedOnlineVideos = cloneDeep(localState.backUpState.onlineVideos || [])
    setDialogState({
      ...clonedMarketing,
      isDiscarded: true,
      images: clonedImages,
      videos: clonedVideos,
      onlineVideos: clonedOnlineVideos
    })
    setLocalState((prevState) => ({
      ...prevState,
      deletedFiles: [],
      filesToUpdate: [],
      discardDialogOpen: false
    }))

    setTimeout(() => {
      setDialogState((prevState) => ({
        ...prevState,
        isDiscarded: false
      }))
    }, 100)
  }

  return (
    <div>
      <IARDialog
        open={isPreviewNotSaveDialogOpen}
        title="Save Preview first"
        content="Please save your changes in preview before publishing"
        onOk={closeNotSavedPreviewDialog}
        onCancel={closeNotSavedPreviewDialog}
        previewNotSaved={true}
      />
      <Dialog className="news-dialog" disableBackdropClick disableEscapeKeyDown open={isOpen}>
        <DialogTitle className="news-dialog-title">
          <span>{title}</span>
          <IconButton aria-label="close" className="close-button">
            <CloseIcon onClick={onClose} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="news-dialog-content">
          <IARDialog
            open={localState.discardDialogOpen}
            title="Confirm"
            content="Do you want to discard your changes?"
            onCancel={() =>
              setLocalState((prevState) => ({
                ...prevState,
                discardDialogOpen: false
              }))
            }
            onOk={handlerDiscardChanges}
          />
          <Grid container>
            <form noValidate autoComplete="off">
              <Grid size={12}>
                {isClone ? (
                  <Grid container className="title-container">
                    <label htmlFor="images" className="body-text">
                      You are cloning the Marketing section "
                      {dialogState && dialogState.emergencyProviderName
                        ? dialogState.emergencyProviderName
                        : nameToClone}
                      " in order to create/edit the Marketing section "
                      {marketingOptions && marketingOptions.emergencyProviderName
                        ? marketingOptions.emergencyProviderName
                        : isNaN(name)
                        ? name
                        : emergencyProviderCloneName}
                      ".
                    </label>
                  </Grid>
                ) : (
                  <></>
                )}
                <Grid container className="title-container">
                  <Grid size={2} className="container-field-label">
                    <label htmlFor="title" className="label-container label-news">
                      Title
                    </label>
                  </Grid>
                  <Grid size={10} className="container-field">
                    <label htmlFor="error" className="error-text">
                      {localState.errorTitle}
                    </label>
                    <TextField
                      id="title"
                      name="title"
                      multiline
                      rows={2}
                      variant="outlined"
                      className="news-textfield"
                      value={dialogState.title}
                      disabled={disableField && !isCreateMarketingSection}
                      onChange={(e) => handleChange(e, setDialogState)}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid size={12} className="container-field  body-text">
                    <label htmlFor="message" className="label-container">
                      Content
                    </label>{' '}
                    <br />
                  </Grid>
                  <Grid size={12} className="container-field body-text">
                    <label htmlFor="error" className="error-text">
                      {localState.errorBodyText}
                    </label>
                    {renderRichTextControl()}
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <div className="container-field dropdown-container">
                    <div>
                      <label htmlFor="positionFiles">Position images and videos</label> <br />
                      {dialogState.positionFiles ? (
                        <CustomDropdown
                          name="positionFiles"
                          value={dialogState.positionFiles}
                          onChange={handleDropdownChange}
                          disableHandleChange={disableField && !isCreateMarketingSection}
                        />
                      ) : (
                        <CustomDropdown
                          name="positionFiles"
                          value={0}
                          onChange={handleDropdownChange}
                          disableHandleChange={disableField && !isCreateMarketingSection}
                        />
                      )}
                    </div>
                  </div>
                </Grid>
                <Grid container>
                  <Grid size={12}>
                    <Grid className="container-field">
                      <label htmlFor="images" className="body-text">
                        Media Files
                      </label>
                      <MediaFilesMessage max={5}></MediaFilesMessage>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid size={6} className="container-field  media-files">
                    <ChooseImage
                      images={dialogState.images}
                      disableHandleClick={disableField && !isCreateMarketingSection}
                      marketingSetter={setDialogState}
                      localMarketingSetter={setLocalState}
                      max={5}
                      elementsAdded={
                        dialogState.images
                          ? dialogState.images.length + dialogState.videos.length
                          : 0
                      }
                      isMessageEnabled={false}
                      marketingDialog={true}
                      setMarketingError={setLocalState}
                    />
                    <>
                      {dialogState.images
                        ? dialogState.images.map((el, index) => {
                            return (
                              <MediaThumbnail
                                disableHandleClick={disableField && !isCreateMarketingSection}
                                type="image"
                                key={[el.fileName, el.caption, index].join('-')}
                                media={el}
                                onUpdate={updateAsset}
                                removeMediaEvent={() =>
                                  removeAsset(
                                    el,
                                    'image',
                                    dialogState.images,
                                    [],
                                    setDialogState,
                                    setLocalState
                                  )
                                }
                              />
                            )
                          })
                        : 0}
                    </>
                  </Grid>
                  <Grid size={6} className="container-field  media-files">
                    {departmentTimeBalance && (
                      <ChooseVideo
                        disableHandleClick={disableField && !isCreateMarketingSection}
                        videos={dialogState.videos}
                        max={5}
                        marketingSetter={setDialogState}
                        marketingDialog={true}
                        localMarketingSetter={setLocalState}
                        elementsAdded={
                          dialogState.images
                            ? dialogState.images.length + dialogState.videos.length
                            : 0
                        }
                        setMarketingError={setLocalState}
                      />
                    )}
                    <>
                      {dialogState.videos
                        ? dialogState.videos.map((el, index) => {
                            return (
                              <MediaThumbnail
                                type="video"
                                disableHandleClick={disableField && !isCreateMarketingSection}
                                key={[el.fileName, el.caption, index].join('-')}
                                media={el}
                                index={index}
                                onUpdate={updateAsset}
                                removeMediaEvent={() =>
                                  removeAsset(
                                    el,
                                    'video',
                                    [],
                                    dialogState.videos,
                                    setDialogState,
                                    setLocalState
                                  )
                                }
                              />
                            )
                          })
                        : 0}
                    </>
                  </Grid>
                  <Grid size={12} className="container-field media-files">
                    <OnlineVideos
                      disableHandleClick={disableField && !isCreateMarketingSection}
                      videos={dialogState.onlineVideos}
                      marketingSetter={setDialogState}
                      marketingDialog={true}
                      localMarketingSetter={setLocalState}
                      max={5}
                    />
                    <>
                      {dialogState.onlineVideos
                        ? dialogState.onlineVideos.length > 0 && (
                            <Grid container>
                              {dialogState.onlineVideos &&
                                dialogState.onlineVideos.map((el, index) => {
                                  return (
                                    <Grid
                                      size={{ xs: 12, sm: 12 }}
                                      className="container-field media-files"
                                      key={[el.fileName, el.caption, index].join('-')}
                                    >
                                      <MediaThumbnail
                                        disableHandleClick={
                                          disableField && !isCreateMarketingSection
                                        }
                                        type="online"
                                        key={[el.fileName, el.caption, index].join('-')}
                                        media={el}
                                        index={index}
                                        removeMediaEvent={removeAsset}
                                        subType={videoTypes.YoutubeVideos}
                                        onUpdate={updateAsset}
                                        removeOnlineVideo={() =>
                                          removeOnlineVideo(
                                            el,
                                            dialogState.onlineVideos,
                                            setDialogState
                                          )
                                        }
                                      />
                                    </Grid>
                                  )
                                })}
                            </Grid>
                          )
                        : 0}
                    </>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid size={12} className="container container-buttons">
                    {mcsToCloneExist ? (
                      <label htmlFor="error" className="error-text">
                        There is already a Marketing section for "
                        {existingMarketingSection && existingMarketingSection.emergencyProviderName
                          ? existingMarketingSection.emergencyProviderName
                          : name}
                        ", if you save this clone the Marketing section for "
                        {existingMarketingSection && existingMarketingSection.emergencyProviderName
                          ? existingMarketingSection.emergencyProviderName
                          : name}
                        " will be overwritten
                      </label>
                    ) : (
                      <></>
                    )}

                    {appConfiguration?.previewEnabled ? (
                      <PrepublishButtons
                        isDiscardDialogOpen={localState.discardDialogOpen}
                        isPreviewDialogOpen={isPreviewDialogOpen}
                        isReadOnlyMember={false}
                        isShown={appConfiguration.previewEnabled}
                        isSaveDialogOpen={isSaveDialogOpen}
                        openPreviewDialog={openPreviewDialog}
                        openDiscardDialog={openDiscardDialog}
                        closePreviewDialog={closePreviewDialog}
                        closeDiscardDialog={closeDiscardDialog}
                        closeSaveDialog={closeSaveDialog}
                        discardChanges={handlerDiscardChanges}
                        disabledButton={disableField && !isCreateMarketingSection}
                        publishButtonValidations={publishButtonValidations}
                        posting={localState.posting}
                        savePreview={handleSavePreview}
                        savePublish={savePublish}
                      />
                    ) : (
                      <div className="save-button-container">
                        <ButtonSave
                          id="saveButton"
                          onClickCallback={
                            isNewVideoEncodingProcessActive
                              ? () => processSectionVideoProcessSaving(false)
                              : save
                          }
                          disabled={disabledButton}
                          loading={localState.posting}
                        />
                        <Button
                          style={{ maxWidth: '250px', minWidth: '250px' }}
                          variant="outlined"
                          id="cancelButton"
                          color="primary"
                          size="medium"
                          disabled={disabledButton}
                          onClick={() =>
                            setLocalState((prevState) => ({
                              ...prevState,
                              discardDialogOpen: true
                            }))
                          }
                        >
                          Discard
                        </Button>
                      </div>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MarketingDialog
